<template>
  <div id="CenterUserSignin" class="flex-1 min-h-0 flex flex-col overflow-hidden">

    <!-- Loading state -->
    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <div class="flex flex-col items-center gap-3">
        <svg class="animate-spin h-6 w-6" style="color: #06C8FF;" xmlns="http://www.w3.org/2000/svg" fill="none"
          viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
          </path>
        </svg>
        <p class="text-[13px] text-muted-foreground">{{ $t('centerusersignin.loading') }}</p>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="fetchError" class="flex-1 flex flex-col items-center justify-center px-7">
      <div class="relative mb-5">
        <div class="w-14 h-14 rounded-full flex items-center justify-center"
          style="background: linear-gradient(145deg, rgba(227, 33, 28, 0.13), rgba(227, 33, 28, 0.04)); border: 1.5px solid rgba(227, 33, 28, 0.16);">
          <div class="w-10 h-10 rounded-full flex items-center justify-center bg-card"
            style="box-shadow: 0 3px 12px rgba(227, 33, 28, 0.14);">
            <ExclamationTriangleIcon class="h-[18px] w-[18px]" style="color: #E3211C;" />
          </div>
        </div>
      </div>
      <h2 class="font-bold text-center text-[18px] tracking-tight text-foreground">
        {{ $t('centerusersignin.error_title') }}
      </h2>
      <p class="text-center text-[13px] leading-relaxed mt-1.5 text-muted-foreground max-w-[260px]">
        {{ $t('centerusersignin.error_description') }}
      </p>
      <button @click="fetchCenter"
        class="flex items-center justify-center gap-2 mt-6 h-[44px] px-6 text-white font-bold text-[14px] rounded-xl active:scale-[0.97] transition-all duration-150 usersignin-cta">
        <ArrowPathIcon class="h-4 w-4 stroke-[2.5]" />
        {{ $t('centerusersignin.retry') }}
      </button>
    </div>

    <!-- Content loaded -->
    <div v-else-if="center" class="flex-1 min-h-0 flex flex-col px-7 lg:px-0 overflow-hidden">

      <!-- Subtitle -->
      <p class="text-[13px] leading-relaxed text-muted-foreground mb-4">
        {{ $t('centerusersignin.subtitle') }}
      </p>

      <!-- Empty state -->
      <div v-if="!center.users || center.users.length === 0"
        class="flex-1 flex flex-col items-center justify-center">
        <div class="w-14 h-14 rounded-full flex items-center justify-center mb-4"
          style="background-color: rgba(6, 200, 255, 0.08);">
          <UserGroupIcon class="h-6 w-6 text-muted-foreground" />
        </div>
        <p class="text-[14px] text-muted-foreground text-center">{{ $t('centerusersignin.no_users') }}</p>
      </div>

      <!-- User grid -->
      <div v-else class="flex-1 min-h-0 overflow-y-auto pb-6 -mx-1 usersignin-scroll">
        <div class="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5 px-1">
          <button v-for="user in center.users" :key="user.id" @click="signinUser = user"
            :data-username="user.name"
            class="relative flex flex-col items-center justify-center rounded-[14px] py-5 px-2 active:scale-[0.96] transition-all duration-150 usersignin-user-card overflow-hidden">
            <!-- Color accent bar at top -->
            <div class="absolute top-0 left-3 right-3 h-[3px] rounded-b-full usersignin-accent-bar" :style="getAccentBarStyle(user.color)"></div>
            <!-- Name -->
            <span class="text-[11.5px] font-bold text-foreground text-center leading-snug break-words w-full">
              {{ user.name }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Password dialog -->
    <PasswordDialog
      :is-open="signinUser != null"
      :title="dialogTitle"
      :user-name="signinUser?.name"
      :accent-color="signinUser?.color"
      v-model="password"
      :placeholder="$t('signin.dialog.password')"
      :error="error"
      :sending="sending"
      :confirm-text="$t('dialogs.confirm')"
      :close-text="$t('dialogs.close')"
      name="center_user_signin"
      @submit="submit"
      @close="closeDialog"
    />
  </div>
</template>

<script setup lang="ts">
import { ArrowPathIcon, ExclamationTriangleIcon, UserGroupIcon } from '@heroicons/vue/24/outline';
import PasswordDialog from '../components/dialogs/PasswordDialog.vue';
import { onMounted, ref, computed, inject, Ref } from 'vue';
import { useUserManagerStore } from '../stores/userManagerStore';
import { useSalesReportsStore } from '../stores/salesReports';
import { GokartsCenter, GokartsConnectionUser } from '../gokarts';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const router = useRouter();
const userManagerStore = useUserManagerStore();
const connectionProgress = inject<Ref<number>>('connectionProgress')!;
const center = ref<GokartsCenter | null>(null);
const loading = ref(true);
const fetchError = ref(false);

const signinUser = ref<GokartsConnectionUser | null>(null);
const sending = ref(false);
const password = ref('');
const error = ref('');

const getAccentBarStyle = (color: string) => {
  const c = color.toLowerCase();
  const isNearBlack = c === '#000000' || c === '#000' || c === '#1a1a1a' || c === '#111111';
  const isNearWhite = c === '#ffffff' || c === '#fff' || c === '#f5f5f5' || c === '#fafafa';
  if (isNearBlack || isNearWhite) {
    return {
      backgroundColor: color,
      boxShadow: `0 2px 8px rgba(128, 128, 128, 0.35)`,
    };
  }
  return {
    backgroundColor: color,
    boxShadow: `0 2px 8px ${color}40`,
  };
};

const closeDialog = () => {
  signinUser.value = null;
  password.value = '';
  error.value = '';
};

const submit = async () => {
  sending.value = true;
  connectionProgress.value = 4;
  const resp = await userManagerStore.connectToCenterUser(signinUser.value?.name!, password.value);

  if (resp instanceof Error) {
    sending.value = false;
    error.value = t(`errors.${resp.message}`);
    return;
  }

  await userManagerStore.getUserInfo();
  await useSalesReportsStore().initCenterComponents();
  router.push({ name: 'Dashboard' });
};

const dialogTitle = computed(() => {
  return t('signin.dialog.title', { name: signinUser.value?.name });
});

const fetchCenter = async () => {
  loading.value = true;
  fetchError.value = false;
  connectionProgress.value = 3;

  console.log('[CenterUserSignin] fetching center...');
  const data = await userManagerStore.getConnectingCenter().catch((err) => {
    console.error('[CenterUserSignin] getConnectingCenter error:', err);
    return err;
  });

  loading.value = false;
  console.log('[CenterUserSignin] response data:', JSON.stringify(data, null, 2));
  console.log('[CenterUserSignin] users:', data?.users);

  if (data instanceof Error) {
    fetchError.value = true;
    return;
  }

  center.value = data;
};

onMounted(fetchCenter);
</script>

<style scoped>
.usersignin-user-card {
  background: #FFFFFF;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  cursor: pointer;
}
.usersignin-accent-bar {
  transition: left 350ms cubic-bezier(0.4, 0, 0.2, 1),
              right 350ms cubic-bezier(0.4, 0, 0.2, 1),
              border-radius 300ms ease-out;
}
@media (hover: hover) {
  .usersignin-user-card:hover .usersignin-accent-bar {
    left: 0 !important;
    right: 0 !important;
    border-radius: 0 !important;
    transition: left 220ms cubic-bezier(0.2, 0, 0, 1),
                right 220ms cubic-bezier(0.2, 0, 0, 1),
                border-radius 180ms ease-out;
  }
}
.usersignin-scroll {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.usersignin-scroll::-webkit-scrollbar {
  display: none;
}
.usersignin-cta {
  background: linear-gradient(135deg, #06C8FF 0%, #009FE3 100%);
  box-shadow: 0 4px 16px rgba(6, 200, 255, 0.25);
}

/* Dark mode */
:is(.dark *).usersignin-user-card {
  background: #1E2024;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}
</style>
